import * as Sentry from '@sentry/browser';

/**
 * ErrorHandler Service
 * Centralized error categorization, formatting, and user-friendly messaging
 */

class ErrorHandler {
  /**
   * Categorize error into specific type for better handling
   * @param {Error|Object} error - Error object from axios or custom
   * @returns {Object} { type, code, message, isCritical, details }
   */
  static categorizeError(error) {
    // Network error (no response from server)
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return {
          type: 'timeout',
          code: 'TIMEOUT',
          message: error.message || 'Request timeout',
          isCritical: true,
          details: error
        };
      }
      return {
        type: 'network',
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error. Please check your connection.',
        isCritical: true,
        details: error
      };
    }

    const { status, data } = error.response;

    // 401 Unauthorized - Token expired (handled by interceptor, but fallback here)
    if (status === 401) {
      return {
        type: 'auth',
        code: 'UNAUTHORIZED',
        message: 'Your session has expired. Please login again.',
        isCritical: true,
        details: error
      };
    }

    // 403 Forbidden - User doesn't have permission
    if (status === 403) {
      return {
        type: 'permission_denied',
        code: 'FORBIDDEN',
        message: (data && data.message) || 'You don\'t have permission to perform this action',
        isCritical: false,
        details: data
      };
    }

    // 400 Bad Request - Validation error or incorrect parameters
    if (status === 400) {
      return {
        type: 'validation',
        code: 'BAD_REQUEST',
        message: (data && data.message) || 'Invalid request. Please check your input.',
        isCritical: false,
        details: (data && data.errors) || data
      };
    }

    // 422 Unprocessable Entity - Business logic error
    if (status === 422) {
      return {
        type: 'unprocessable',
        code: 'UNPROCESSABLE',
        message: (data && data.message) || 'Cannot process this request',
        isCritical: false,
        details: data
      };
    }

    // 500+ Server error
    if (status >= 500) {
      return {
        type: 'server_error',
        code: `SERVER_ERROR_${status}`,
        message: 'Server error. Please try again later.',
        isCritical: true,
        details: error
      };
    }

    // Unknown error
    return {
      type: 'unknown',
      code: `ERROR_${status}`,
      message: (data && data.message) || 'An error occurred. Please try again.',
      isCritical: status >= 500,
      details: data || error
    };
  }

  /**
   * Check if response contains an error (for API responses that return errors in data)
   * @param {Object} response - Response data
   * @returns {boolean}
   */
  static hasErrorInResponse(response) {
    if (!response) return false;
    return response.error === true || response.code !== undefined || (response.message && response.message.includes('error'));
  }

  /**
   * Get user-friendly error message
   * @param {Error|Object} error - Error object
   * @param {string} context - Context (e.g., 'payout', 'registry', 'transaction')
   * @returns {string} User-friendly message
   */
  static getMessageForError(error, context = 'operation') {
    const errorInfo = this.categorizeError(error);

    // Context-specific messages
    const contextMessages = {
      payout: {
        permission_denied: 'You don\'t have permission to perform payouts on this merchant',
        timeout: 'Payment operation timed out. Please try again.',
        network: 'Network error occurred during payment. Please check your connection and try again.',
        validation: 'Payment details are invalid. Please check and try again.',
        server_error: 'Payment server error. Please try again later.',
        unprocessable: 'Cannot process this payment. The merchant may not be configured properly.'
      },
      registry: {
        permission_denied: 'You don\'t have permission to work with this registry\'s merchant',
        timeout: 'Registry operation timed out. Please try again.',
        network: 'Network error occurred during registry operation.',
        validation: 'Registry data is invalid. Please check the file and try again.',
        server_error: 'Registry server error. Please try again later.',
        unprocessable: 'Registry cannot be processed. Please contact support if this persists.'
      },
      transaction: {
        permission_denied: 'You don\'t have permission to modify this transaction',
        timeout: 'Transaction operation timed out.',
        network: 'Network error occurred during transaction operation.',
        validation: 'Transaction data is invalid.',
        server_error: 'Transaction server error. Please try again later.',
        unprocessable: 'Cannot process this transaction.'
      },
      download: {
        permission_denied: 'You don\'t have permission to download this file',
        timeout: 'Download timed out. Please try again.',
        network: 'Network error occurred during download.',
        server_error: 'Server error occurred during download. Please try again later.'
      }
    };

    const messages = contextMessages[context] || {};
    return messages[errorInfo.type] || errorInfo.message;
  }

  /**
   * Handle merchant access error specifically
   * @param {string} merchantUUID - Merchant UUID
   * @returns {Error}
   */
  static handleMerchantAccessError(merchantUUID) {
    const error = new Error('You don\'t have access to this merchant');
    error.type = 'permission_denied';
    error.merchantUUID = merchantUUID;
    return error;
  }

  /**
   * Check if user has specific permission for a merchant
   * @param {string} permission - Permission code (e.g., 'PAYOUT_REGISTRY_PROCESS')
   * @param {string} merchantUUID - Merchant UUID
   * @param {Array} accountAccessSettings - User's account access settings from JWT
   * @returns {boolean}
   */
  static hasPermission(permission, merchantUUID, accountAccessSettings = []) {
    if (!accountAccessSettings || accountAccessSettings.length === 0) {
      return false;
    }

    // Find merchant access settings
    const merchantAccess = accountAccessSettings.find(
      access => access.merchantUUID === merchantUUID
    );

    if (!merchantAccess) {
      return false;
    }

    // Permission bitmasks (from PermissionsEditor)
    const PERMISSIONS = {
      TRANSACTION_CANCELLING: 1,
      TRANSACTION_LIMIT_SETTING: 2,
      STATISTICS_ACCESS: 4,
      MAIL_ALERT_SETTING: 8,
      PAYOUT_REGISTRY_PROCESS: 16,
      PAYOUT_REGISTRY_VIEW: 32,
      PAYOUT_REGISTRY_REPORT: 64,
      PAYOUT_REGISTRIES_LIST: 128,
      // Add more as needed
    };

    const permissionBit = PERMISSIONS[permission];
    if (!permissionBit) return false;

    return (merchantAccess.privileges & permissionBit) === permissionBit;
  }

  /**
   * Log error for debugging (can be extended to send to monitoring service)
   * @param {Error} error - Error object
   * @param {Object} context - Additional context { action, userId, merchantId, etc. }
   */
  static logError(error, context = {}) {
    const errorInfo = this.categorizeError(error);
    const timestamp = new Date().toISOString();

    const logData = {
      timestamp,
      type: errorInfo.type,
      code: errorInfo.code,
      message: errorInfo.message,
      context,
      details: errorInfo.details
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorHandler]', logData);
    }

    // Send errors to Sentry
    if (errorInfo.isCritical) {
      Sentry.captureException(error, {
        extra: logData,
        tags: {
          error_type: errorInfo.type,
          error_code: errorInfo.code,
          context: context.action || 'unknown'
        }
      });
    }
  }

  /**
   * Format error for API response validation
   * @param {Object} apiResponse - Response from API
   * @returns {Object|null} Error object or null if no error
   */
  static extractApiError(apiResponse) {
    if (!apiResponse) return null;

    // Pattern 1: error flag
    if (apiResponse.error === true) {
      return {
        type: 'api_error',
        code: apiResponse.code,
        message: apiResponse.message || 'API error occurred',
        details: apiResponse
      };
    }

    // Pattern 2: code field indicates error
    if (apiResponse.code && apiResponse.code !== 0 && apiResponse.code !== '0') {
      return {
        type: 'api_error',
        code: apiResponse.code,
        message: apiResponse.message || 'API error occurred',
        details: apiResponse
      };
    }

    return null;
  }
}

export default ErrorHandler;



// WEBPACK FOOTER //
// ./src/Services/ErrorHandler.js